import {
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Button,
  Divider,
  Tooltip,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import { Card, CardHeader, CardContent } from '@/components/ui/card';

// Define types for our component
interface Activity {
  id: string;
  type: 'document' | 'email' | 'meeting' | 'task' | 'case' | 'comment' | 'edit';
  title: string;
  description?: string;
  timestamp: Date;
  user?: {
    id: string;
    name: string;
    avatar?: string;
  };
  caseId?: string;
  caseName?: string;
  documentId?: string;
  documentName?: string;
  taskId?: string;
  taskName?: string;
  meetingId?: string;
  meetingName?: string;
  emailId?: string;
  emailSubject?: string;
}

interface RecentActivitiesProps {
  onSelectCase?: (caseId: string) => void;
  onSelectDocument?: (documentId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onSelectMeeting?: (meetingId: string) => void;
  onSelectEmail?: (emailId: string) => void;
  maxItems?: number;
  title?: string;
}

const RecentActivities: React.FC<RecentActivitiesProps> = ({
  onSelectCase,
  onSelectDocument,
  onSelectTask,
  onSelectMeeting,
  onSelectEmail,
  maxItems = 5,
  title = 'Recent Activities',
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch activities on component mount
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);

      // Simulate API call delay
      setTimeout(() => {
        const mockActivities: Activity[] = [
          {
            id: 'activity1',
            type: 'document',
            title: 'New document uploaded',
            description: 'Smith_Contract_v3.pdf was uploaded to Smith case',
            timestamp: new Date(2025, 4, 20, 10, 15), // Today
            user: {
              id: 'user1',
              name: 'Jonathan Dawson',
            },
            caseId: 'case123',
            caseName: 'Smith v. MegaCorp',
            documentId: 'doc1',
            documentName: 'Smith_Contract_v3.pdf',
          },
          {
            id: 'activity2',
            type: 'email',
            title: 'Email received',
            description: 'Court Filing Confirmation for Johnson case',
            timestamp: new Date(2025, 4, 20, 9, 30), // Today
            user: {
              id: 'user2',
              name: 'Court Filing System',
            },
            caseId: 'case456',
            caseName: 'Johnson v. TechCorp',
            emailId: 'email1',
            emailSubject: 'Court Filing Confirmation',
          },
          {
            id: 'activity3',
            type: 'task',
            title: 'Task completed',
            description: 'Review contract amendments for Smith case',
            timestamp: new Date(2025, 4, 19, 16, 45), // Yesterday
            user: {
              id: 'user3',
              name: 'Alexandra Hart',
            },
            caseId: 'case123',
            caseName: 'Smith v. MegaCorp',
            taskId: 'task1',
            taskName: 'Review contract amendments',
          },
          {
            id: 'activity4',
            type: 'meeting',
            title: 'Meeting scheduled',
            description: 'Client consultation for Williams Estate',
            timestamp: new Date(2025, 4, 19, 14, 20), // Yesterday
            user: {
              id: 'user3',
              name: 'Alexandra Hart',
            },
            caseId: 'case789',
            caseName: 'Williams Estate',
            meetingId: 'meeting1',
            meetingName: 'Client Consultation',
          },
          {
            id: 'activity5',
            type: 'comment',
            title: 'Comment added',
            description: 'Added comment to Al-Wasi contract review',
            timestamp: new Date(2025, 4, 19, 11, 10), // Yesterday
            user: {
              id: 'user4',
              name: 'Sarah Johnson',
            },
            caseId: 'case101',
            caseName: 'Al-Wasi International',
            documentId: 'doc2',
            documentName: 'Al-Wasi_Contract_Review.docx',
          },
          {
            id: 'activity6',
            type: 'edit',
            title: 'Document edited',
            description: 'Edited motion to dismiss for Mitchell case',
            timestamp: new Date(2025, 4, 18, 15, 30), // 2 days ago
            user: {
              id: 'user3',
              name: 'Alexandra Hart',
            },
            caseId: 'case202',
            caseName: 'Mitchell v. StateBank',
            documentId: 'doc3',
            documentName: 'Motion_to_Dismiss.docx',
          },
          {
            id: 'activity7',
            type: 'case',
            title: 'New case created',
            description: 'Created new case: Bennett Consultation',
            timestamp: new Date(2025, 4, 18, 10, 45), // 2 days ago
            user: {
              id: 'user3',
              name: 'Alexandra Hart',
            },
            caseId: 'case303',
            caseName: 'Bennett Consultation',
          },
        ];

        setActivities(mockActivities);
        setLoading(false);
      }, 800);
    };

    fetchActivities();
  }, []);

  // Limit the number of activities if maxItems is provided
  const limitedActivities = maxItems ? activities.slice(0, maxItems) : activities;

  const handleRefresh = () => {
    setLoading(true);

    // Simulate refresh delay
    setTimeout(() => {
      setLoading(false);
    }, 800);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <DescriptionIcon />;
      case 'email':
        return <EmailIcon />;
      case 'meeting':
        return <EventIcon />;
      case 'task':
        return <AssignmentIcon />;
      case 'case':
        return <GavelIcon />;
      case 'comment':
        return <CommentIcon />;
      case 'edit':
        return <EditIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'document':
        return 'primary.main';
      case 'email':
        return 'info.main';
      case 'meeting':
        return 'warning.main';
      case 'task':
        return 'success.main';
      case 'case':
        return 'secondary.main';
      case 'comment':
        return 'info.main';
      case 'edit':
        return 'primary.main';
      default:
        return 'primary.main';
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleActivityClick = (activity: Activity) => {
    switch (activity.type) {
      case 'document':
        if (onSelectDocument && activity.documentId) {
          onSelectDocument(activity.documentId);
        }
        break;
      case 'email':
        if (onSelectEmail && activity.emailId) {
          onSelectEmail(activity.emailId);
        }
        break;
      case 'meeting':
        if (onSelectMeeting && activity.meetingId) {
          onSelectMeeting(activity.meetingId);
        }
        break;
      case 'task':
        if (onSelectTask && activity.taskId) {
          onSelectTask(activity.taskId);
        }
        break;
      case 'case':
        if (onSelectCase && activity.caseId) {
          onSelectCase(activity.caseId);
        }
        break;
      case 'comment':
      case 'edit':
        if (onSelectDocument && activity.documentId) {
          onSelectDocument(activity.documentId);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardHeader>
      <CardContent>
        <List sx={{ p: 0 }}>
          {limitedActivities.map(activity => (
            <ListItem
              key={activity.id}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 0,
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: getActivityColor(activity.type),
                    width: 32,
                    height: 32,
                  }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={activity.title}
                secondary={
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • {formatTimestamp(activity.timestamp)}
                    </Typography>
                  </Box>
                }
              />
              <IconButton size="small">
                <MoreVertIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default RecentActivities;
